import { useContext, useEffect, useState } from "react";

import {
  Routes,
  Route,
} from "react-router-dom";

import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject
} from "@apollo/client";

import { useIdleTimer } from "react-idle-timer";

import graphqlAuthSetup from "lib/api/graphqlAuthSetup";
import { AuthContext } from "lib/context/auth";

import Login from "modules/public/Login";
import HardwareDashboard from "modules/hardware-dashboard/HardwareDashboard";
import ChargerDetail from "modules/charger/ChargerDetail";
import Settings from "modules/settings/Settings";
import ServiceErrorList from "modules/service-error-list/ServiceErrorList";
import ChargerLogsList from "modules/charger-logs-list/ChargerLogsList";
import ChargerList from "modules/charger-list/ChargerList";
import ConfigManager from "modules/config-manager/ConfigManager";
import UserList from "modules/user-list/UserList";
import AddUser from "modules/forms/add-user/AddUser";
import SiteManager from "modules/site-manager/SiteManager";
import NotFound from "modules/public/NotFound";
import GlobalNotificationModal from "components/GlobalNotificationModal";

import { Loading } from "@evos/components";

import type { ReactNode } from "react";

import "assets/scss/custom.scss";
import { useKeycloak } from "@react-keycloak/web";

const MONITORING_ROLE = "Monitoring";
 
// Get token from local context, if it exists
function useAuth() {
  return useContext(AuthContext);
}

export default function App() {
  const auth = useAuth();
  const { keycloak, initialized } = useKeycloak();
  const darkMode = false;

  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject> | null>(null);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    graphqlAuthSetup(auth).then((clients) => {
      setClient(clients.client);
    });
  }, [auth, initialized, auth?.user?.token]);

  useIdleTimer({
    timeout: 1000 * 60 * 20, // 20 minutes
    onIdle: () => {
      if (auth?.isIdle === false) {
        auth?.setIsIdle?.(true);
      }
    },
    onActive: () => {
      setTimeout(() => {
        auth?.setIsIdle?.(false);
      }, 5000);
    },
    disabled: keycloak?.authenticated !== true
  });

  return (
    <>
      {client != null && (
        <ApolloProvider client={client}>
          <div
            id="backgroundColor"
            style={{
              backgroundColor: darkMode ? "#495057" : "#f5f5f5",
              color: darkMode ? "white" : "black"
            }}
          >
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <Settings />
                  </RequireAuth>
                }
              />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <HardwareDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/hardware-dashboard"
                element={
                  <RequireAuth>
                    <HardwareDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/charger-list"
                element={
                  <RequireAuth>
                    <ChargerList />
                  </RequireAuth>
                }
              />
              <Route
                path="/config-manager"
                element={
                  <RequireAuth>
                    <ConfigManager />
                  </RequireAuth>
                }
              />
              <Route
                path="/charger-logs-list"
                element={
                  <RequireAuth>
                    <ChargerLogsList />
                  </RequireAuth>
                }
              />
              <Route
                path="/service-error-list"
                element={
                  <RequireAuth>
                    <ServiceErrorList />
                  </RequireAuth>
                }
              />
              <Route
                path="/user-list"
                element={
                  <RequireAuth>
                    <UserList />
                  </RequireAuth>
                }
              />
              <Route
                path="/site-manager"
                element={
                  <RequireAuth>
                    <SiteManager />
                  </RequireAuth>
                }
              />
              <Route
                path="/user-list/add"
                element={
                  <RequireAuth>
                    <AddUser />
                  </RequireAuth>
                }
              />
              <Route
                path="/c/:serial"
                element={
                  <RequireAuth>
                    <ChargerDetail />
                  </RequireAuth>
                }
              />
              <Route path="/*" element={<NotFound />} />
            </Routes>
            <GlobalNotificationModal
              expiredTokenError={auth?.expiredTokenError}
              isIdle={auth?.isIdle}
              unknownUserError={auth?.unknownUserError}
            />
          </div>
        </ApolloProvider>
      )}
    </>
  );
}

type RequireAuthProps = {
  children: ReactNode;
  requiredRole?: string;
};

function RequireAuth({ children, requiredRole = MONITORING_ROLE }: RequireAuthProps) {
  const auth = useAuth();
  const { keycloak, initialized } = useKeycloak();

  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (keycloak?.authenticated !== true) {
      keycloak?.login({ redirectUri: window.location.href });
      return;
    }

    if (auth?.user == null && keycloak?.tokenParsed != null) {
      const tokenParsed = keycloak.tokenParsed;
      const roles = ((tokenParsed.realm_access?.roles ?? []) as string[])
        .filter((role): role is string => typeof role === "string" && role.length > 0);

      const mappedUser = {
        given_name: tokenParsed.given_name ?? tokenParsed.name ?? tokenParsed.preferred_username ?? "",
        email: tokenParsed.email ?? "",
        sub: tokenParsed.sub ?? "",
        roles,
        token: keycloak?.token ?? undefined,
      };

      auth?.signin(mappedUser, () => {});
    }
  }, [auth, initialized, keycloak]);

  const currentUser = auth?.user;

  if (!initialized || currentUser == null) {
    return <Loading />;
  }

  const currentUserRoles = (currentUser.roles ?? []).map((role) => role.toLowerCase());
  const required = requiredRole.toLowerCase();

  if (!currentUserRoles.includes(required)) {
    return <NotFound />;
  }

  return <>{children}</>;
}
